import { useState } from "react";
import { Container, Typography, Box, Grid, Paper, Link, TextField, Button, Snackbar, Alert } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BadgeIcon from "@mui/icons-material/Badge";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useLang } from "../context/LanguageContext";
import logo from "../components/logo.svg";

export default function Kontakt() {
  const { t } = useLang();
  const [form, setForm] = useState<{ name: string; email: string; phone: string; message: string }>({ name: "", email: "", phone: "", message: "" });
  const [snack, setSnack] = useState<{ open: boolean; severity: "success" | "error" | "info"; message: string }>({ open: false, severity: "success", message: "" });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setSnack({ open: true, severity: "error", message: t("contact.nameRequired") });
      return;
    }
    window.location.href = `mailto:mazurmasz@gmail.com?subject=Zapytanie od ${encodeURIComponent(form.name)}&body=Imię: ${encodeURIComponent(form.name)}%0AE-mail: ${encodeURIComponent(form.email)}%0ATelefon: ${encodeURIComponent(form.phone)}%0A%0A${encodeURIComponent(form.message)}`;
    setSnack({ open: true, severity: "info", message: t("contact.mailInfo") });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <Box sx={{ backgroundColor: "#1a1a1a", minHeight: "100vh" }}>
      <Box sx={{ height: 64 }} />

      <Box
        sx={{
          py: 10,
          background: "linear-gradient(135deg, #1c1c1c 0%, #222 100%)",
          color: "#f0f0f0",
          textAlign: "center",
        }}
      >
        <Container>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {t("contact.title")}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, mt: 1 }}>
            {t("contact.subtitle")}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ textAlign: "center", mt: 6 }}>
        <img src={logo} alt="Mazur Masz" height={120} />
      </Box>

      <Container sx={{ py: 8 }}>
        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, backgroundColor: "#2a2a2a", color: "#f0f0f0", border: "1px solid #444" }}>
              <Typography variant="h5" gutterBottom sx={{ color: "#1976d2", fontWeight: 700 }}>
                {t("contact.detailsTitle")}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
                <EmailIcon sx={{ color: "#1976d2" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>{t("contact.emailLabel")}</Typography>
                  <Link href="mailto:mazurmasz@gmail.com" variant="body1"
                    sx={{ color: "#f0f0f0", textDecoration: "none", "&:hover": { textDecoration: "underline", color: "#1976d2" } }}>
                    mazurmasz@gmail.com
                  </Link>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
                <PhoneIcon sx={{ color: "#1976d2" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>{t("contact.phoneLabel")}</Typography>
                  <Typography variant="body1">505 321 328</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
                <LocationOnIcon sx={{ color: "#1976d2" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>{t("contact.addressLabel")}</Typography>
                  <Typography variant="body1">95-030 Rzgów, Woj. Łódzkie</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
                <AccessTimeIcon sx={{ color: "#1976d2" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>{t("contact.hoursLabel")}</Typography>
                  <Typography variant="body1">{t("contact.hoursWeekday")}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>{t("contact.hoursWeekend")}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
                <BadgeIcon sx={{ color: "#1976d2" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>{t("contact.companyLabel")}</Typography>
                  <Typography variant="body1">MAZURMASZ Paweł Mazur</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>NIP: 731 198 96 08 &nbsp;|&nbsp; REGON: 101411713</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #444" }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {t("contact.warranty")}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, backgroundColor: "#2a2a2a", color: "#f0f0f0", border: "1px solid #444" }}>
              <Typography variant="h5" gutterBottom sx={{ color: "#1976d2", fontWeight: 700 }}>
                {t("contact.formTitle")}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <TextField
                  label={t("contact.name")}
                  fullWidth
                  required
                  value={form.name}
                  onChange={handleChange("name")}
                  sx={{ mb: 2, input: { color: "#f0f0f0" }, label: { color: "#aaa" } }}
                />
                <TextField
                  label={t("contact.email")}
                  type="email"
                  fullWidth
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                  sx={{ mb: 2, input: { color: "#f0f0f0" }, label: { color: "#aaa" } }}
                />
                <TextField
                  label={t("contact.phone")}
                  fullWidth
                  value={form.phone}
                  onChange={handleChange("phone")}
                  sx={{ mb: 2, input: { color: "#f0f0f0" }, label: { color: "#aaa" } }}
                />
                <TextField
                  label={t("contact.message")}
                  multiline
                  rows={5}
                  fullWidth
                  required
                  value={form.message}
                  onChange={handleChange("message")}
                  sx={{ mb: 2, textarea: { color: "#f0f0f0" }, label: { color: "#aaa" } }}
                />
                <Button type="submit" variant="contained" size="large" fullWidth
                  sx={{ backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#125ea8" } }}>
                  {t("contact.send")}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 0, backgroundColor: "#2a2a2a", color: "#f0f0f0", border: "1px solid #444", overflow: "hidden", filter: "invert(0.9) hue-rotate(180deg)" }}>
              <iframe
                src="https://www.google.com/maps?q=Prawda+61,+95-030+Rzg%C3%B3w&output=embed"
                width="100%"
                height="450"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map"
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
